import { redirectToPaymentGatewayAction } from "../../../../Actions/LogicActions/Checkout/RedirectToPaymentGatewayAction";
import { getCmsConfirmationContentAction } from "../../../../Actions/LogicActions/CmsConfirmationContent/GetCmsConfirmationContentAction";
import { guestPaymentProcessAction } from "../../../../Actions/UIActions/Checkout/Payment/GuestPaymentProcessAction";
import { paymentProcessActionInstance } from "../../../../Actions/UIActions/Checkout/Payment/PaymentProcessAction";
import { paymentTypeChangeAction } from "../../../../Actions/UIActions/Checkout/Payment/PaymentTypeChangeAction";
import { quickExchangePaymentProcessAction } from "../../../../Actions/UIActions/Checkout/Payment/QuickExchangePaymentProcessAction";
import { updateSaveCardAction, IUpdateSaveCardActionParams } from "../../../../Actions/UIActions/Checkout/Payment/UpdateSaveCardAction";
import { ExecutionStatus } from "../../../../core/enum/ExecutionStatus";
import { _eventBus } from "../../../../core/events/EventBus";
import { StepDefinition } from "../../../../core/flows/StepDefinition";
import { StepFlow } from "../../../../core/flows/StepFlow";
import { STORAGE_KEYS } from "../../../../Lib/Constants/storageKeys";
import { isActionRunSuccessfully } from "../../../../Lib/Utils/validationUtil";
import { checkoutTree } from "../../../../Orchestrator/ConstantTree/CheckoutPage/CheckoutTree";
import { IAppliedPassengerPassAtomType } from "../../../../Orchestrator/Recoils/BookingAtom";
import { getRecoilStateValue, getRecoilStateValueWithType, updateRecoilState } from "../../../../Orchestrator/Recoils/Events/Recoil";
import { IGetConfirmationContentActionParams } from "../../../../Orchestrator/Types/Index";
import { IPaymentProcessGuestParams } from "../../../../Orchestrator/Types/Interface/ActionParams/BookingDetailActionParams";
import { IPaymentProcessActionParams, IQuickExchangePaymentProcessActionParams, IRedirectToPaymentGatewayParams } from "../../../../Orchestrator/Types/Interface/IPaymentProcess";
import { PaymentContext } from "../Context/PaymentContext";

class PaymentInitStep extends StepFlow {
  get stepName() {
    return "PaymentInit_Step";
  }

  async onEventSetup(): Promise<void> {
    // FlowRunner re-instantiates the step on every flow re-entry, so prior-
    // instance handlers may still be attached to `_eventBus`. Drain them via
    // the base-class static registry before registering fresh ones — this
    // covers both cross-instance and same-instance resume cases. Without it,
    // every click of **Pay** fan-outs to N accumulated handlers → N
    // concurrent `POST /api/payment/session` requests on the same click.
    await this.drainPersistentSubscriptions();

    this.registerPersistentEventSubscription(
      _eventBus.on<string, void>({
        eventName: checkoutTree.Payment.Change,
        handler: async ({ data }) => {
          let context = this.getCtx<PaymentContext>("paymentContext")!;
          if (context) {
            context.paymentMethod = data || "";
          }
          await paymentTypeChangeAction.setParams(context.paymentMethod);
          await paymentTypeChangeAction.execute();
        },
      })
    );

    this.registerPersistentEventSubscription(
      _eventBus.on<IPaymentProcessActionParams, void>({
        eventName: checkoutTree.Payment.Click,
        handler: async (payload) => {
          updateRecoilState("paymentSubmittingAtom", true);
          let didEmitGatewayRedirect = false;
          try {
            paymentProcessActionInstance.setParams(payload.data!);
            await paymentProcessActionInstance.execute();
            const appliedPassengerPassAtom = getRecoilStateValueWithType("appliedPassengerPassAtom")
            const hasActivePass = [...(appliedPassengerPassAtom?.outbound || []), ...(appliedPassengerPassAtom?.inbound || [])].length > 0;

            if (isActionRunSuccessfully(paymentProcessActionInstance)) {
              const result = paymentProcessActionInstance.result!
              const paymentSummary: any = getRecoilStateValue("checkoutPaymentSummaryAtom")
              const isManageSeating: boolean = getRecoilStateValue("isManageSeatingAtom") || false
              const isQuickExchange: boolean = (getRecoilStateValue("isQuickExchangeStateAtom") as boolean) || false
              const paymentTotal = Number(paymentSummary?.payment?.total)
              const hasNonNegativePaymentTotal = Number.isFinite(paymentTotal) && paymentTotal >= 0
              // Quick-exchange is on the redirect whitelist so Confirm reliably
              // emits Payment.RedirectToGateway → /api/payment/session, even if
              // `paymentSummary.payment.total` momentarily reads as NaN/undefined
              // (e.g. a hydration race). Without this clause, Confirm fell
              // through to the ROUTE_CHANGE branch below and silently navigated
              // to /bookings/details without ever opening MPGS — the original
              // "silent redirect on Confirm" bug. NOT the same as the
              // removed-in-iteration-3 fee-fallback amount guard.
              const shouldRedirectToGateway = result === "success" && (
                localStorage.getItem("isUpgradeToPremiumFlow") === "true" ||
                hasActivePass ||
                localStorage.getItem(STORAGE_KEYS.CHANGE_DATE_FLOW) == "true" ||
                hasNonNegativePaymentTotal ||
                isManageSeating ||
                isQuickExchange
              );

              // Clear applied passenger pass and resume search on successful checkout
              updateRecoilState("appliedPassengerPassAtom", null);
              localStorage.removeItem("resumeSearch");

              if (shouldRedirectToGateway) {
                const { accessToken, customer, bookingId, paymentDetails } = payload.data!
                const amount = Math.max(0, paymentDetails?.total || 0).toFixed(2);

                didEmitGatewayRedirect = true;
                _eventBus.emit({
                  eventName: checkoutTree.Payment.RedirectToGateway,
                  data: {
                    amount,
                    mail: customer.email,
                    bookingId,
                    accessToken
                  },
                });
              } else {
                _eventBus.emit({
                  eventName: "ROUTE_CHANGE",
                  data: `/bookings/details/${payload.data?.bookingId}`,
                });
              }
            }
          } finally {
            // Hold the submitting flag through the gateway redirect — that
            // handler clears it on lightbox open/cancel/error. Otherwise
            // clear here so the user can retry on validation failure.
            if (!didEmitGatewayRedirect) {
              updateRecoilState("paymentSubmittingAtom", false);
            }
          }
        },
      })
    );

    this.registerPersistentEventSubscription(
      _eventBus.on<IQuickExchangePaymentProcessActionParams, void>({
        eventName: checkoutTree.QuickExchangePayment.Click,
        handler: async (payload) => {
          updateRecoilState("paymentSubmittingAtom", true);
          let didNavigate = false;
          try {
            quickExchangePaymentProcessAction.setParams(payload.data!);
            await quickExchangePaymentProcessAction.execute();

            if (quickExchangePaymentProcessAction.status.executionStatus == ExecutionStatus.SUCCESS) {
              _eventBus.emit({
                eventName: "FLOW_CLOSE",
                data: `AfterSalesBookingFlow`,
              });

              didNavigate = true;
              _eventBus.emit({
                eventName: "ROUTE_CHANGE",
                data: `/bookings/details/${payload.data?.bookingId}`,
              });

            }
          } finally {
            if (!didNavigate) {
              updateRecoilState("paymentSubmittingAtom", false);
            }
          }
        },
      })
    );

    this.registerPersistentEventSubscription(
      _eventBus.on<IRedirectToPaymentGatewayParams, void>({
        eventName: checkoutTree.Payment.RedirectToGateway,
        handler: async (payload) => {
          try {
            redirectToPaymentGatewayAction.setParams(payload.data!);
            await redirectToPaymentGatewayAction.execute();
            // Lightbox opens in-place — no route change needed
          } finally {
            // Re-enable Pay so the user can retry if the redirect failed or
            // the lightbox is dismissed. On success the lightbox owns the
            // screen until MPGS navigates back to /checkout/callback.
            updateRecoilState("paymentSubmittingAtom", false);
          }
        },
      })
    );

    this.registerPersistentEventSubscription(
      _eventBus.on<IPaymentProcessGuestParams, void>({
        eventName: checkoutTree.Guest.Payment.Click,
        handler: async (payload) => {
          updateRecoilState("paymentSubmittingAtom", true);
          let didEmitGatewayRedirect = false;
          try {
            guestPaymentProcessAction.setParams(payload.data!);
            await guestPaymentProcessAction.execute();
            if (guestPaymentProcessAction.status.executionStatus == ExecutionStatus.SUCCESS) {
              const { accessToken, customer, bookingId, paymentDetails } = payload.data!
              const amount = Math.max(0, paymentDetails?.total || 0).toFixed(2);

              didEmitGatewayRedirect = true;
              _eventBus.emit({
                eventName: checkoutTree.Payment.RedirectToGateway,
                data: {
                  amount,
                  mail: customer.email,
                  bookingId,
                  accessToken
                },
              });
            }
          } finally {
            if (!didEmitGatewayRedirect) {
              updateRecoilState("paymentSubmittingAtom", false);
            }
          }
        },
      })
    );

    this.registerPersistentEventSubscription(
      _eventBus.on<IGetConfirmationContentActionParams, void>({
        eventName: checkoutTree.Confim.Click,
        handler: async (payload) => {
          getCmsConfirmationContentAction.setParams({ slug: payload.data?.slug! });
          getCmsConfirmationContentAction.execute()
        },
      })
    );

    this.registerPersistentEventSubscription(
      _eventBus.on<IUpdateSaveCardActionParams, void>({
        eventName: checkoutTree.Payment.SaveCard.Change,
        handler: async (payload) => {
          updateSaveCardAction.setParams(payload.data!);
          await updateSaveCardAction.execute();
        },
      })
    );
  }

  async onExecute(): Promise<void> {
    this.setCtx("paymentContext", PaymentContext);
    console.log("Payment Initial Step Executed.....");
    updateRecoilState("saveCardAtom", false);
  }
}

export const PaymentInitStepDeclaration = new StepDefinition({
  stepName: "PaymentInit_Step",
  stepClass: PaymentInitStep,
});
